import { api } from './config';
import { ApiResponse } from '../types';

export type ShippingCarrier = 'fedex' | 'dhl' | 'ups' | 'ems' | 'other';
export type ShippingStatus =
  | 'pending'
  | 'label_created'
  | 'picked_up'
  | 'in_transit'
  | 'out_for_delivery'
  | 'delivered'
  | 'exception'
  | 'returned';

export interface ShippingResponse {
  id: number;
  sample_request?: {
    id: number;
    request_code: string;
    product_name: string;
    company_name: string;
  };
  manufacturer?: {
    id: number;
    code: string;
    company_name: string;
  };
  lab_number?: string;
  sample_images?: string[];
  carrier: ShippingCarrier;
  carrier_name: string;
  tracking_number?: string;
  tracking_url?: string;
  shipping_status: ShippingStatus;
  shipping_status_name: string;
  fedex_tracking_data?: Record<string, any>;
  last_tracking_update?: string;
  shipped_at?: string;
  estimated_delivery_at?: string;
  delivered_at?: string;
  recipient_name?: string;
  recipient_address?: string;
  recipient_phone?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface ShippingCreateRequest {
  sample_request_id: number;
  manufacturer_id?: number;
  lab_number?: string;
  sample_images?: string[];
  carrier?: ShippingCarrier;
  tracking_number?: string;
  recipient_name?: string;
  recipient_address?: string;
  recipient_phone?: string;
  notes?: string;
}

export interface ShippingUpdateRequest {
  lab_number?: string;
  sample_images?: string[];
  carrier?: ShippingCarrier;
  tracking_number?: string;
  shipping_status?: ShippingStatus;
  shipped_at?: string;
  estimated_delivery_at?: string;
  recipient_name?: string;
  recipient_address?: string;
  recipient_phone?: string;
  notes?: string;
}

// Helper function to convert snake_case to camelCase
const toCamelCase = (data: any): any => {
  if (data === null || data === undefined) return data;
  if (Array.isArray(data)) return data.map(toCamelCase);
  if (typeof data !== 'object') return data;

  const result: Record<string, any> = {};
  for (const key in data) {
    const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
    result[camelKey] = toCamelCase(data[key]);
  }
  return result;
};

export const shippingApi = {
  // ========== Admin Endpoints ==========

  /**
   * Create shipping info (Admin)
   */
  create: async (data: ShippingCreateRequest): Promise<{ data: ApiResponse<ShippingResponse> }> => {
    const response = await api.post<ApiResponse<ShippingResponse>>('/admin/shipping', data);
    return response;
  },

  /**
   * Update shipping info (Admin)
   */
  update: async (id: number, data: ShippingUpdateRequest): Promise<{ data: ApiResponse<ShippingResponse> }> => {
    const response = await api.put<ApiResponse<ShippingResponse>>(`/admin/shipping/${id}`, data);
    return response;
  },

  /**
   * Get shipping by ID (Admin)
   */
  getById: async (id: number): Promise<{ data: ApiResponse<ShippingResponse> }> => {
    const response = await api.get<ApiResponse<ShippingResponse>>(`/admin/shipping/${id}`);
    return response;
  },

  /**
   * Get shipping list by sample request ID (Admin)
   */
  getBySampleRequest: async (sampleRequestId: number): Promise<{ data: ApiResponse<ShippingResponse[]> }> => {
    const response = await api.get<ApiResponse<ShippingResponse[]>>(`/admin/shipping/sample-request/${sampleRequestId}`);
    return response;
  },

  /**
   * Refresh FedEx tracking (Admin)
   */
  refreshTracking: async (id: number): Promise<{ data: ApiResponse<ShippingResponse> }> => {
    const response = await api.post<ApiResponse<ShippingResponse>>(`/admin/shipping/${id}/refresh-tracking`);
    return response;
  },

  /**
   * Delete shipping (Admin)
   */
  delete: async (id: number): Promise<{ data: ApiResponse<void> }> => {
    const response = await api.delete<ApiResponse<void>>(`/admin/shipping/${id}`);
    return response;
  },

  /**
   * Upsert shipping info (Admin)
   * Creates new shipping if not exists, updates if exists for the sample request
   */
  upsert: async (data: ShippingCreateRequest): Promise<{ data: ApiResponse<ShippingResponse> }> => {
    const response = await api.post<ApiResponse<ShippingResponse>>('/admin/shipping/upsert', data);
    return response;
  },

  /**
   * Batch upsert shipping info (Admin)
   * Creates/updates multiple shipping records at once
   */
  batchUpsert: async (data: ShippingCreateRequest[]): Promise<{ data: ApiResponse<ShippingResponse[]> }> => {
    const response = await api.post<ApiResponse<ShippingResponse[]>>('/admin/shipping/batch', data);
    return response;
  },

  // ========== Brand/Public Endpoints ==========

  /**
   * Get shipping info for a sample request (Brand)
   */
  getShippingForRequest: async (sampleRequestId: number): Promise<{ data: ApiResponse<ShippingResponse[]> }> => {
    const response = await api.get<ApiResponse<ShippingResponse[]>>(`/sample-requests/${sampleRequestId}/shipping`);
    return response;
  },

  /**
   * Track shipping by tracking number (Public)
   */
  trackByNumber: async (trackingNumber: string): Promise<{ data: ApiResponse<ShippingResponse> }> => {
    const response = await api.get<ApiResponse<ShippingResponse>>(`/sample-requests/shipping/track/${trackingNumber}`);
    return response;
  },

  /**
   * Refresh tracking for brand user
   */
  refreshTrackingForBrand: async (sampleRequestId: number, shippingId: number): Promise<{ data: ApiResponse<ShippingResponse> }> => {
    const response = await api.post<ApiResponse<ShippingResponse>>(
      `/sample-requests/${sampleRequestId}/shipping/${shippingId}/refresh-tracking`
    );
    return response;
  },
};

// Status display helpers
export const getShippingStatusColor = (status: ShippingStatus): string => {
  const colors: Record<ShippingStatus, string> = {
    pending: 'bg-gray-100 text-gray-800',
    label_created: 'bg-blue-100 text-blue-800',
    picked_up: 'bg-indigo-100 text-indigo-800',
    in_transit: 'bg-yellow-100 text-yellow-800',
    out_for_delivery: 'bg-orange-100 text-orange-800',
    delivered: 'bg-green-100 text-green-800',
    exception: 'bg-red-100 text-red-800',
    returned: 'bg-purple-100 text-purple-800',
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
};

export const getShippingStatusText = (status: ShippingStatus): string => {
  const texts: Record<ShippingStatus, string> = {
    pending: 'Pending',
    label_created: 'Label Created',
    picked_up: 'Picked Up',
    in_transit: 'In Transit',
    out_for_delivery: 'Out for Delivery',
    delivered: 'Delivered',
    exception: 'Exception',
    returned: 'Returned',
  };
  return texts[status] || status;
};

export const getCarrierName = (carrier: ShippingCarrier): string => {
  const names: Record<ShippingCarrier, string> = {
    fedex: 'FedEx',
    dhl: 'DHL',
    ups: 'UPS',
    ems: 'EMS',
    other: 'Other',
  };
  return names[carrier] || carrier;
};
